import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
    Users, TrendingUp, Calendar, DollarSign, Search, 
    Building2, Eye, Trash2, RefreshCw, Shield, LogOut, 
    CreditCard, History
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminPage() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [salons, setSalons] = useState([]);
    const [filteredSalons, setFilteredSalons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const token = localStorage.getItem('superadmin_token');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin/login');
            return;
        }
        loadData();
    }, [token]);

    useEffect(() => {
        filterSalons();
    }, [searchTerm, statusFilter, salons]);

    const loadData = async () => {
        try {
            const [statsRes, salonsRes] = await Promise.all([
                axios.get(`${API_BASE}/api/superadmin/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/api/superadmin/salons`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setStats(statsRes.data);
            setSalons(salonsRes.data);
        } catch (error) {
            toast.error("Failed to load data");
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('superadmin_token');
                navigate('/superadmin/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterSalons = () => {
        let filtered = salons;
        
        if (statusFilter !== "all") {
            filtered = filtered.filter(s => s.subscription_status === statusFilter);
        }
        
        if (searchTerm) {
            filtered = filtered.filter(s => 
                s.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.owner_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        setFilteredSalons(filtered);
    };

    const handleViewSalon = (salonId) => {
        navigate(`/superadmin/salons/${salonId}`);
    };

    const handleDeleteSalon = async (salonId, salonName) => {
        if (!confirm(`Are you sure you want to delete "${salonName}"? This will delete all their data permanently.`)) {
            return;
        }
        
        try {
            await axios.delete(`${API_BASE}/api/superadmin/salons/${salonId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Salon deleted successfully");
            loadData();
        } catch (error) {
            toast.error("Failed to delete salon");
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('superadmin_token');
        navigate('/superadmin/login');
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'trial': return 'bg-blue-100 text-blue-800';
            case 'expired': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-3xl font-bold">SuperAdmin Dashboard</h1>
                        <p className="text-muted-foreground">Platform Overview & Management</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={() => navigate('/superadmin/admins')} variant="outline">
                            <Shield className="w-4 h-4 mr-2" />
                            Manage Admins
                        </Button>
                        <Button onClick={() => navigate('/superadmin/transactions')} variant="outline">
                            <CreditCard className="w-4 h-4 mr-2" />
                            Transactions
                        </Button>
                        <Button onClick={() => navigate('/superadmin/logs')} variant="outline">
                            <History className="w-4 h-4 mr-2" />
                            Activity Logs
                        </Button>
                        <Button onClick={loadData} variant="outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                        <Button onClick={handleLogout} variant="ghost">
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium">Total Salons</CardTitle>
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_salons || 0}</div>
                            <p className="text-xs text-muted-foreground">
                                +{stats?.recent_signups || 0} this month
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
                            <TrendingUp className="w-4 h-4 text-green-600" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.active_salons || 0}</div>
                            <p className="text-xs text-muted-foreground">
                                {stats?.trial_salons || 0} on trial, {stats?.expired_salons || 0} expired
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium">Total Appointments</CardTitle>
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_appointments || 0}</div>
                            <p className="text-xs text-muted-foreground">
                                Across all salons
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
                            <Users className="w-4 h-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_clients || 0}</div>
                            <p className="text-xs text-muted-foreground">
                                Platform wide
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <Card>
                    <CardHeader>
                        <CardTitle>Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Button 
                                variant="outline" 
                                className="h-20 flex flex-col items-center justify-center gap-2"
                                onClick={() => navigate('/superadmin/admins')}
                            >
                                <Shield className="w-6 h-6" />
                                <span>Manage Admins</span>
                            </Button>
                            <Button 
                                variant="outline" 
                                className="h-20 flex flex-col items-center justify-center gap-2"
                                onClick={() => navigate('/superadmin/transactions')}
                            >
                                <CreditCard className="w-6 h-6" />
                                <span>View Transactions</span>
                            </Button>
                            <Button 
                                variant="outline" 
                                className="h-20 flex flex-col items-center justify-center gap-2"
                                onClick={() => navigate('/superadmin/logs')}
                            >
                                <History className="w-6 h-6" />
                                <span>Activity Logs</span>
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Salons Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Manage Salons</CardTitle>
                        <div className="flex gap-4 mt-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search salons..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="trial">Trial</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {filteredSalons.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No salons found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left p-4">Salon</th>
                                            <th className="text-left p-4">Owner</th>
                                            <th className="text-left p-4">Status</th>
                                            <th className="text-left p-4">Clients</th>
                                            <th className="text-left p-4">Appointments</th>
                                            <th className="text-left p-4">Staff</th>
                                            <th className="text-left p-4">Joined</th>
                                            <th className="text-right p-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSalons.map((salon) => (
                                            <tr key={salon.id} className="border-b hover:bg-accent/50 transition-colors">
                                                <td className="p-4">
                                                    <div>
                                                        <p className="font-medium">{salon.business_name}</p>
                                                        <p className="text-sm text-muted-foreground">{salon.email}</p>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div>
                                                        <p>{salon.owner_name}</p>
                                                        {salon.phone && (
                                                            <p className="text-sm text-muted-foreground">{salon.phone}</p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <Badge className={getStatusColor(salon.subscription_status)}>
                                                        {salon.subscription_status}
                                                    </Badge>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-1">
                                                        <Users className="w-4 h-4 text-muted-foreground" />
                                                        <span>{salon.client_count || 0}</span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                                        <span>{salon.appointment_count || 0}</span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-1">
                                                        <Users className="w-4 h-4 text-muted-foreground" />
                                                        <span>{salon.staff_count || 0}</span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div>
                                                        <p>{new Date(salon.created_at).toLocaleDateString()}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {new Date(salon.created_at).toLocaleTimeString()}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleViewSalon(salon.id)}
                                                            title="View Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleDeleteSalon(salon.id, salon.business_name)}
                                                            title="Delete Salon"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-destructive" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Summary Footer */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <p>
                                Showing {filteredSalons.length} of {salons.length} salon{salons.length !== 1 ? 's' : ''}
                            </p>
                            <p>
                                Last updated: {new Date().toLocaleTimeString()}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}